/**
 * Backplan List
 *
 * Workspace-level list of Back Plan documents. From here users can
 * create a new Back Plan or navigate into an existing one.
 *
 * Route: /prioritization/backplan
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  ArrowRight,
  Calendar,
  Target,
  Loader2,
  AlertCircle,
  Map,
  ChevronDown,
  ChevronUp,
  Circle,
  ArrowDown,
  Flag,
  Layers,
  Clock,
  LayoutList,
  Table2,
  Check,
  PenLine,
  Trash2,
} from 'lucide-react';
import { getItemCountState } from '../../components/collection/types';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';
import { useBackplanStore } from '../../stores/prioritization/backplanStore';
import { useDesiredOutcomeStore } from '../../stores/prioritization/desiredOutcomeStore';

// ── Glossary data ──────────────────────────────────────────────

const TERMS = [
  {
    term: 'Goal Node',
    icon: <Flag className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'The desired future state you\'re planning backward from. Every Back Plan has at least one goal node — it\'s the "end" of the tree and the starting point of your thinking.',
  },
  {
    term: 'Prerequisite Condition',
    icon: <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'A state that must be true before a parent condition or goal can be achieved. Ask "what must be true for this to happen?" to generate prerequisites. Each answer becomes a child node.',
  },
  {
    term: 'Dependency Edge',
    icon: <ArrowDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'The directed connection from a prerequisite to a goal. A → B means "A must be true for B to become achievable." Edges are cycle-safe — the system prevents circular dependencies.',
  },
  {
    term: 'Node Status',
    icon: <Clock className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />,
    def: 'Each condition tracks its current truth value: Unknown (default), True (already achieved), In Progress (underway), or Not True (confirmed gap). Tracking status turns a Back Plan into a live readiness map.',
  },
  {
    term: 'Tense',
    icon: <span className="text-xs font-mono text-[var(--text-ter)] flex-shrink-0 mt-0.5">T</span>,
    def: 'The grammatical frame for how a condition is written: Past ("We secured Series A"), Present ("We have 10 engineers"), or Future ("Customer retention exceeds 90%"). Using past tense for achieved conditions and future tense for aspirational ones makes the tree easier to read.',
  },
  {
    term: 'Future Point',
    icon: <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'The time horizon the Back Plan is aimed at — e.g., "18–36 months," "end of FY26," or "post-Series A." It anchors the scope of the goal node and helps size the prerequisite tree appropriately.',
  },
  {
    term: 'Defined Success',
    icon: <Target className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0 mt-0.5" />,
    def: 'The specific, measurable criteria that confirm the goal node has been met. Where the goal node names the desired state, defined success names how you\'ll know it\'s real.',
  },
  {
    term: 'Swimlane',
    icon: <Layers className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />,
    def: 'A horizontal grouping used in Grid view to organize nodes by team, department, or workstream. Swimlanes answer "who owns this condition?" without changing the dependency structure.',
  },
  {
    term: 'Milestone Column',
    icon: <span className="text-xs font-mono text-[var(--text-ter)] flex-shrink-0 mt-0.5">M</span>,
    def: 'A vertical column in Grid view representing a time checkpoint or stage gate — e.g., "Seed Close," "Beta Launch," "Series A." Nodes placed in a milestone column represent conditions that must be true by that checkpoint.',
  },
];

const STEPS = [
  { n: 1, title: 'Name your future point', body: 'Decide on the time horizon first — 6 months, 18 months, 3 years. This scopes everything that follows.' },
  { n: 2, title: 'Write the goal node', body: 'State the desired future condition in present or past tense as if it\'s already true: "We have closed Series A." Make it concrete enough that you\'d know if it happened.' },
  { n: 3, title: 'Ask: "What must be true for this to happen?"', body: 'Generate prerequisite nodes for the goal. Each answer is a child node connected by a dependency edge. Aim for 2–5 prerequisites per node.' },
  { n: 4, title: 'Recurse until you reach today', body: 'For each prerequisite, repeat step 3. Stop when you reach conditions your team can act on in the next sprint or quarter — these are your actionable leaves.' },
  { n: 5, title: 'Assign status to each node', body: 'Mark conditions as True, In Progress, or Not True. This turns the tree into a readiness map showing exactly where the critical path is blocked.' },
  { n: 6, title: '(Optional) Add swimlanes and milestones', body: 'In Grid view, organize nodes into horizontal swimlanes (by team) and vertical milestone columns (by time checkpoint) to add organizational clarity.' },
];

// ── Collapsible about section ──────────────────────────────────

function AboutBackPlans() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'how' | 'terms'>('how');

  return (
    <div className="mb-6 border border-[var(--slate)] dark:border-[var(--slate)]/60 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/30 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Map className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-sec)]">
            What is a Back Plan? How does it work?
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
          : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
        }
      </button>

      {open && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-t border-[var(--slate)] dark:border-[var(--slate)]/40">
          {/* Lede */}
          <div className="px-5 py-4 border-b border-[var(--rule)]">
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed max-w-3xl">
              A <strong>Back Plan</strong> is a reverse-chronological outcome planning tool. Instead of asking
              "what should we do next?", it asks <em>"what must be true for our goal to be achievable?"</em> and
              works backward from a desired future state, layer by layer, until you arrive at conditions
              your team can act on today. The result is a dependency tree — a precise map of what needs to
              be in place, in what order, before the goal can be reached.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[var(--rule)] px-5 pt-3">
            <button
              onClick={() => setTab('how')}
              className={`pb-2 mr-5 text-sm font-medium border-b-2 transition-colors ${
                tab === 'how'
                  ? 'border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              How to create one
            </button>
            <button
              onClick={() => setTab('terms')}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'terms'
                  ? 'border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              Key terms
            </button>
          </div>

          {/* How to create */}
          {tab === 'how' && (
            <div className="px-5 py-4">
              <ol className="space-y-4 max-w-2xl">
                {STEPS.map((s) => (
                  <li key={s.n} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-xs font-bold flex items-center justify-center mt-0.5">
                      {s.n}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{s.title}</p>
                      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Key terms */}
          {tab === 'terms' && (
            <div className="px-5 py-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-4xl">
                {TERMS.map((t) => (
                  <div key={t.term} className="flex gap-2.5">
                    {t.icon}
                    <div>
                      <dt className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{t.term}</dt>
                      <dd className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{t.def}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Table row ──────────────────────────────────────────────────

function BackplanTableRow({
  doc,
  onDelete,
  deletingId,
}: {
  doc: ReturnType<typeof useBackplanStore>['documents'][number];
  onDelete: (doc: ReturnType<typeof useBackplanStore>['documents'][number]) => void;
  deletingId: string | null;
}) {
  return (
    <tr className="border-b border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40 transition-colors">
      <td className="px-4 py-2.5">
        <Link to={`/prioritization/backplan/${doc.id}`}
          className="flex items-center gap-2 group">
          <Map className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          <span className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] transition-colors truncate max-w-xs">
            {doc.title}
          </span>
        </Link>
        {doc.objective && (
          <p className="text-xs text-[var(--text-ter)] ml-5 mt-0.5 truncate max-w-sm">{doc.objective}</p>
        )}
      </td>
      <td className="px-3 py-2.5 w-32">
        {doc.futurePoint
          ? <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]"><Calendar className="h-3 w-3" />{doc.futurePoint}</span>
          : <span className="text-xs text-[var(--text-ter)]">—</span>}
      </td>
      <td className="px-3 py-2.5 max-w-[160px]">
        <span className="text-xs text-[var(--text-ter)] truncate block">{doc.definedSuccess || '—'}</span>
      </td>
      <td className="px-2 py-2.5 text-right">
        <button
          type="button"
          onClick={() => onDelete(doc)}
          disabled={deletingId === doc.id}
          title="Delete Back Plan"
          className="inline-flex items-center justify-center p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors disabled:opacity-50"
        >
          {deletingId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </td>
      <td className="px-3 py-2.5 w-8 text-right">
        <Link to={`/prioritization/backplan/${doc.id}`} className="inline-flex text-[var(--text-ter)] hover:text-[var(--text-ter)]">
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </td>
    </tr>
  );
}

// ── Main page ──────────────────────────────────────────────────

export default function BackplanList() {
  const navigate = useNavigate();
  const { documents, isLoading, error, fetchDocuments, createBackplan, deleteBackplan } = useBackplanStore();
  const { outcomes, fetchOutcomes } = useDesiredOutcomeStore();

  const [isCreating, setIsCreating]   = useState(false);
  const [newTitle, setNewTitle]       = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [viewMode, setViewMode]       = useState<'cards' | 'table' | null>(null);
  const [pickerOpen, setPickerOpen]   = useState(false);
  const [customMode, setCustomMode]   = useState(false);
  const [deletingId, setDeletingId]   = useState<string | null>(null);

  async function handleDeleteDocument(doc: (typeof documents)[number]) {
    if (
      !window.confirm(
        `Delete “${doc.title}”? This removes the Back Plan and all its nodes, edges, swimlanes, and milestones. This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeletingId(doc.id);
    const { success, error: delErr } = await deleteBackplan(doc.id);
    setDeletingId(null);
    if (!success) {
      alert(delErr ?? 'Could not delete Back Plan');
    }
  }

  useEffect(() => {
    fetchDocuments();
    fetchOutcomes();
  }, [fetchDocuments, fetchOutcomes]);

  const countState  = getItemCountState(documents.length);
  const effectiveView = viewMode ?? (countState === 'many' ? 'table' : 'cards');

  async function handleCreate(title: string) {
    if (!title.trim()) return;
    setIsCreating(true);
    const { backplan, error: createError } = await createBackplan({ title: title.trim() });
    setIsCreating(false);
    if (createError || !backplan) {
      alert(`Failed to create Back Plan: ${createError ?? 'Unknown error'}`);
      return;
    }
    navigate(`/prioritization/backplan/${backplan.id}`);
  }

  function handleSelectOutcome(title: string) {
    setPickerOpen(false);
    handleCreate(title);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Map className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30"
        title="Back Plans"
        subtitle="Reverse-chronological outcome planning — start from the future and work backward"
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="backplan_list" />
            {documents.length > 0 && (
              <div className="flex items-center border border-[var(--rule)] overflow-hidden">
                <button onClick={() => setViewMode('cards')} title="Card view"
                  className={`p-1.5 transition-colors ${effectiveView === 'cards' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <LayoutList className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setViewMode('table')} title="Table view"
                  className={`p-1.5 transition-colors ${effectiveView === 'table' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <Table2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Back Plan
            </button>
          </div>
        }
      />

      {/* About / instructions */}
      <AboutBackPlans />

      {/* Create form */}
      {showNewForm && (
        <div className="mb-6 p-5 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20">
          <p className="text-sm font-semibold text-[var(--text-pri)] mb-1">
            What outcome are you planning backward from?
          </p>
          <p className="text-xs text-[var(--text-sec)] mb-4">
            Select an existing desired outcome or describe your own.
          </p>

          {/* Existing outcomes picker */}
          {outcomes.length > 0 && !customMode && (
            <div className="relative mb-3">
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                className={[
                  'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm  border transition-colors text-left',
                  'bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-purple-500',
                  pickerOpen ? 'border-[var(--slate)]' : 'border-[var(--slate)] dark:border-[var(--slate)] hover:border-[var(--slate)]',
                ].join(' ')}
              >
                <span className="text-[var(--text-ter)] italic">Select an existing desired outcome…</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
              </button>

              {pickerOpen && (
                <>
                  <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setPickerOpen(false)} />
                  <div className="absolute z-50 mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg overflow-hidden">
                    <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                      {outcomes.map((o) => (
                        <li key={o.id}>
                          <button
                            type="button"
                            onClick={() => handleSelectOutcome(o.title)}
                            disabled={isCreating}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-left text-[var(--text-pri)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
                          >
                            <span className="truncate">{o.title}</span>
                            {isCreating ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)] flex-shrink-0" /> : <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Divider if both options present */}
          {outcomes.length > 0 && !customMode && (
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-[var(--slate-dim)] dark:bg-[var(--slate)]/60" />
              <button
                type="button"
                onClick={() => { setCustomMode(true); setNewTitle(''); }}
                className="flex items-center gap-1.5 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium"
              >
                <PenLine className="h-3 w-3" />
                Write my own
              </button>
              <div className="flex-1 h-px bg-[var(--slate-dim)] dark:bg-[var(--slate)]/60" />
            </div>
          )}

          {/* Custom text entry — always shown when no outcomes exist; toggled otherwise */}
          {(outcomes.length === 0 || customMode) && (
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate(newTitle);
                  if (e.key === 'Escape') { if (customMode && outcomes.length > 0) { setCustomMode(false); } else { setShowNewForm(false); } }
                }}
                placeholder="e.g. Series A closed, 10k active users, full-stack delivery team in place"
                className="flex-1 px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all"
              />
              <button
                onClick={() => handleCreate(newTitle)}
                disabled={!newTitle.trim() || isCreating}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-sm font-medium transition-colors"
              >
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Create
              </button>
              {customMode && outcomes.length > 0 && (
                <button
                  onClick={() => setCustomMode(false)}
                  className="px-3 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-sec)]"
                >
                  Back
                </button>
              )}
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button
              onClick={() => { setShowNewForm(false); setCustomMode(false); setNewTitle(''); }}
              className="text-xs text-[var(--text-sec)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading...
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-[var(--coral)] dark:text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={fetchDocuments} className="ml-auto underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && documents.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[var(--rule)]">
          <Map className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
          <h3 className="text-base font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
            No Back Plans yet
          </h3>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4 max-w-sm mx-auto">
            Start by naming the future state you want to make real — then work backward from it.
          </p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first Back Plan
          </button>
        </div>
      )}

      {/* List */}
      {!isLoading && !error && documents.length > 0 && (
        <>
          {effectiveView === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="relative group/card border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:shadow-md hover:border-[var(--slate)] dark:hover:border-[var(--slate)] transition-all"
                >
                  <Link
                    to={`/prioritization/backplan/${doc.id}`}
                    className="block p-5 pr-12"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate group-hover/card:text-[var(--text-ter)] dark:group-hover/card:text-[var(--text-ter)] transition-colors">
                          {doc.title}
                        </h3>
                        {doc.objective && (
                          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 line-clamp-2">
                            {doc.objective}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover/card:text-[var(--text-ter)] flex-shrink-0 mt-0.5 transition-colors" />
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {doc.futurePoint && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{doc.futurePoint}</span>
                        </div>
                      )}
                      {doc.definedSuccess && (
                        <div className="flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          <span className="truncate max-w-[160px]">{doc.definedSuccess}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDeleteDocument(doc)}
                    disabled={deletingId === doc.id}
                    title="Delete Back Plan"
                    className="absolute top-3 right-3 inline-flex items-center justify-center p-2 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors disabled:opacity-50"
                  >
                    {deletingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {effectiveView === 'table' && (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Back Plan</th>
                    <th className="px-3 py-2 w-32 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Future Point</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Defined Success</th>
                    <th className="w-10 px-2 py-2 text-right text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide" title="Delete"> </th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <BackplanTableRow key={doc.id} doc={doc} onDelete={handleDeleteDocument} deletingId={deletingId} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
